/**
 * Paginação, ordenação e envelope de listagem (`01-architecture.md §6`).
 *
 * Contrato obrigatório em **toda** listagem (RN-50): nenhum endpoint devolve
 * coleção ilimitada. O modo padrão do núcleo operacional é **cursor**, porque
 * `OFFSET 40000` faz o Postgres varrer 40 mil linhas para descartá-las e porque
 * cursor não pula nem repete registro quando chega lead novo durante a navegação.
 *
 * O cursor é **opaco**: base64url de `{"v":1,"chave":[<valor ordenado>, <id>]}`.
 * Sempre composto com o `id`, que desempata — sem desempate, keyset não é
 * confiável em campo com valores repetidos (e `status` repete muito).
 *
 * Este módulo é puro: codifica, decodifica e valida. A tradução para `where`/
 * `orderBy` do Prisma é da API (`comum/consulta/keyset.ts`), porque depende do
 * cliente de banco e o pacote compartilhado não importa Prisma.
 */
import { z } from 'zod'

export const PAGINACAO_TAMANHO_PADRAO = 50
export const PAGINACAO_TAMANHO_MAXIMO = 200

/** Versão do formato do cursor. Cursor de versão desconhecida é rejeitado. */
const VERSAO_CURSOR = 1

/**
 * Valor do campo de ordenação no cursor. `null` é um valor legítimo:
 * `ultimaInteracaoEm` e `proximoRetornoEm` são nulos em lead que nunca foi
 * tocado, e a página seguinte precisa saber que paramos na região dos nulos.
 */
export type ValorCursor = string | null

export interface Cursor {
  readonly valor: ValorCursor
  /** `id` da última linha da página anterior, como string (PKs são bigint). */
  readonly id: string
}

export function codificarCursor(cursor: Cursor): string {
  const conteudo = JSON.stringify({ v: VERSAO_CURSOR, chave: [cursor.valor, cursor.id] })
  return Buffer.from(conteudo, 'utf8').toString('base64url')
}

/**
 * Devolve `null` para cursor ilegível — quem chama traduz para `422`. Nunca
 * lança: cursor inválido é entrada do usuário, não defeito do servidor.
 */
export function decodificarCursor(bruto: string): Cursor | null {
  try {
    const texto = Buffer.from(bruto, 'base64url').toString('utf8')
    const objeto = JSON.parse(texto) as { v?: unknown; chave?: unknown }

    if (objeto.v !== VERSAO_CURSOR || !Array.isArray(objeto.chave) || objeto.chave.length !== 2) {
      return null
    }

    const [valor, id] = objeto.chave as [unknown, unknown]
    if (typeof id !== 'string' || !/^\d+$/.test(id)) return null
    if (valor !== null && typeof valor !== 'string') return null

    return { valor, id }
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Ordenação
// ---------------------------------------------------------------------------

export type Direcao = 'asc' | 'desc'

export interface Ordenacao<Campo extends string = string> {
  readonly campo: Campo
  readonly direcao: Direcao
}

/**
 * `-chegadaEm` → `{ campo: 'chegadaEm', direcao: 'desc' }`.
 *
 * Um campo só, e não a lista que o `§6.6` permite: com keyset, cada campo extra
 * de ordenação multiplica os termos do `OR` do cursor, e nenhuma tela do MVP
 * ordena por duas colunas. Aceitar `-chegadaEm,clienteNome` e paginar por só uma
 * delas devolveria página com item repetido — pior do que recusar.
 */
export function interpretarOrdenacao<Campo extends string>(
  bruto: string | undefined,
  permitidos: ReadonlyArray<Campo>,
  padrao: Ordenacao<Campo>,
): Ordenacao<Campo> | 'CAMPO_NAO_PERMITIDO' {
  if (bruto === undefined || bruto.trim() === '') return padrao

  const texto = bruto.trim()
  const direcao: Direcao = texto.startsWith('-') ? 'desc' : 'asc'
  const campo = texto.replace(/^[-+]/, '')

  if (!permitidos.includes(campo as Campo)) return 'CAMPO_NAO_PERMITIDO'
  return { campo: campo as Campo, direcao }
}

// ---------------------------------------------------------------------------
// Envelope
// ---------------------------------------------------------------------------

export interface Paginacao {
  readonly limite: number
  /** `null` na última página. */
  readonly proximoCursor: string | null
  readonly temMais: boolean
  /** `null` por padrão (§6.3): `COUNT(*)` custa quase o mesmo que a consulta. */
  readonly total: number | null
}

export interface EnvelopeListagem<T> {
  readonly dados: ReadonlyArray<T>
  readonly paginacao: Paginacao
}

export interface MetaAgregado {
  /** Obrigatório em resposta de agregado (RNF-80): a tela precisa datar o número. */
  readonly atualizadoEm: string
  readonly defasagemMinutos: number
}

export interface EnvelopeAgregado<T> {
  readonly dados: T
  readonly meta: MetaAgregado
}

// ---------------------------------------------------------------------------
// Envelope por offset — organização e catálogos
// ---------------------------------------------------------------------------

/**
 * `03-endpoints-draft.md §3`, última linha: **catálogos e organização são as únicas
 * listagens em paginação por offset** — "volume pequeno e a interface mostra
 * numeração".
 *
 * A exceção se justifica pelos dois motivos que tornam o cursor obrigatório no
 * núcleo, e que aqui não valem:
 *
 *  - `OFFSET 40000` é caro porque o Postgres varre para descartar. Unidades,
 *    equipes e catálogos têm dezenas de linhas, não dezenas de milhares.
 *  - Cursor não pula nem repete quando chega registro novo durante a navegação.
 *    Catálogo não recebe registro novo enquanto o administrador o navega — quem
 *    cadastra é ele mesmo, na mesma tela.
 *
 * E o que o offset dá em troca é o que estas telas precisam: numeração de página e
 * `total` sempre presente. `COUNT(*)` aqui custa o que custa contar 40 linhas.
 */
export interface PaginacaoOffset {
  readonly limite: number
  /** 1-based, como a interface numera. */
  readonly pagina: number
  /** Sempre presente nesta família (ao contrário do cursor, onde é `null`). */
  readonly total: number
  readonly temMais: boolean
}

export interface EnvelopeOffset<T> {
  readonly dados: ReadonlyArray<T>
  readonly paginacao: PaginacaoOffset
}

/** Monta o envelope a partir do que o repositório devolveu. */
export function montarEnvelopeOffset<T>(
  dados: ReadonlyArray<T>,
  paginacao: { limite: number; pagina: number },
  total: number,
): EnvelopeOffset<T> {
  return {
    dados,
    paginacao: {
      limite: paginacao.limite,
      pagina: paginacao.pagina,
      total,
      temMais: paginacao.pagina * paginacao.limite < total,
    },
  }
}

// ---------------------------------------------------------------------------
// Schemas reutilizáveis
// ---------------------------------------------------------------------------

/**
 * `limite` acima do máximo é **rejeitado**, não reduzido em silêncio: reduzir faz
 * o cliente acreditar que recebeu tudo (§6.4). O código de erro é
 * `LIMITE_PAGINACAO_EXCEDIDO`, traduzido pelo caso de uso.
 */
export const schemaLimite = z.coerce
  .number()
  .int('Informe um número inteiro de itens por página.')
  .min(1, 'O limite mínimo é 1 item.')
  .max(PAGINACAO_TAMANHO_MAXIMO, `O limite máximo é ${PAGINACAO_TAMANHO_MAXIMO} itens por página.`)
  .default(PAGINACAO_TAMANHO_PADRAO)

export const schemaCursor = z.string().trim().min(1).max(512).optional()

export const schemaOrdenar = z
  .string()
  .trim()
  .max(60)
  .regex(/^-?[A-Za-z][A-Za-z0-9]*$/, 'Ordenação inválida. Use `campo` ou `-campo`.')
  .optional()

export const schemaPaginacaoCursor = z.object({
  limite: schemaLimite,
  cursor: schemaCursor,
  ordenar: schemaOrdenar,
})

/** Página 1-based. Ver `PaginacaoOffset` para por que esta família não usa cursor. */
export const schemaPagina = z.coerce
  .number()
  .int('Informe um número inteiro de página.')
  .min(1, 'A primeira página é 1.')
  .max(100_000, 'Página fora do intervalo.')
  .default(1)

export const schemaPaginacaoOffset = z.object({
  limite: schemaLimite,
  pagina: schemaPagina,
  ordenar: schemaOrdenar,
})

/** Lista separada por vírgula (`status=EM_ABERTO,EM_ATENDIMENTO` → `IN`, §6.5). */
export function schemaListaDeEnum<T extends string>(
  valores: ReadonlyArray<T>,
  rotulo: string,
): z.ZodType<T[] | undefined, z.ZodTypeDef, string | undefined> {
  return z
    .string()
    .trim()
    .min(1)
    .optional()
    .transform((bruto, contexto) => {
      if (bruto === undefined) return undefined

      const itens = bruto
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0)

      const invalidos = itens.filter((item) => !valores.includes(item as T))
      if (invalidos.length > 0 || itens.length === 0) {
        contexto.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${rotulo} inválido: ${invalidos.join(', ') || '(vazio)'}.`,
        })
        return z.NEVER
      }

      return itens as T[]
    })
}
